import React, { useCallback } from "react";
import styled from "styled-components";
import { AnyObject, Box, SaveDataBuilders, TextEditor, utils } from "@thenounproject/lingo-core";

import { CanvasComponentProps, CanvasEditableProps } from "./sharedProps";
import ToolbarColorMenu from "../../../../app/_components/ToolbarColorMenu";
import useBatchSaveItems from "@actions/items/useBatchSaveItems";
import useShowModal, { ModalTypes } from "@redux/actions/useModals";
import { getVersionedItemId } from "@src/helpers/items";
import { insertData, InsertType } from "@constants/InsertType";
import { getCanvasLayoutProperties } from "./utils";
import { SelectionWrapper } from "./SelectionWrapper";
import { CanvasItemWrapper } from "./CanvasItemWrapper";
import { OverflowButton } from "./OverflowButton";

const Wrapper = styled(Box)`
  position: relative;
  ${utils.showOnHoverCSS("action-button")}
`;

const NoteWrapper = styled(Box)<{ canEdit?: boolean; isEditing?: boolean }>`
  flex: 1 1 100%;
  transition: min-height 0.4s ease;
  ${p => p.canEdit && !p.isEditing && `cursor: pointer;`}
`;

type Props = CanvasComponentProps & CanvasEditableProps;

const CanvasNote: React.FC<Props> = ({
  item,
  isSelected,
  onClick,
  draggable,
  onDragStart,
  onDragEnd,
  onOverflowSelected,
  canEdit,
  canGenerateText,
  isEditing,
  onFinishEditing,
  onBeginEditing,
  onDoubleClick: _onDoubleClick,
  index,
  containerWidth,
  ...rest
}) => {
  const { outerWidth, size } = getCanvasLayoutProperties({ item, containerWidth });
  const versionedId = getVersionedItemId(item);
  const hasDefaultValue = item.data.content === insertData[InsertType.note].data.content;

  const [batchSaveItems] = useBatchSaveItems();
  const { showModal } = useShowModal();

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      onClick(item, e);
    },
    [onClick, item]
  );

  const handleOverflowClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onOverflowSelected(item);
    },
    [item, onOverflowSelected]
  );

  const handleFinishEditing = useCallback(
    (data: AnyObject | null) => {
      onFinishEditing(item, data);
    },
    [item, onFinishEditing]
  );

  const handleAIGenerate = useCallback(() => {
    showModal(ModalTypes.GENERATE_TEXT_CONTENT, {
      item,
      buildSaveData: SaveDataBuilders.noteContent,
    });
  }, [showModal, item]);

  return (
    <CanvasItemWrapper
      data-full-width={outerWidth === "100%" || size === 1}
      outerWidth={outerWidth}
      size={size}
      index={index}
      itemId={item.id}
      {...rest}>
      <Wrapper
        data-selected={isSelected}
        id={item.id}
        data-itemid={versionedId}
        data-dragid={versionedId}
        data-shortid={item.shortId}
        position="relative"
        draggable={draggable}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}>
        <SelectionWrapper selected={isSelected}>
          <NoteWrapper data-testid="gallery-note-wrapper" canEdit={canEdit} isEditing={isEditing}>
            <Box borderRadius="default" data-testid="gallery-note-text">
              <TextEditor
                itemId={item.id}
                content={item.data?.content}
                buildSaveData={SaveDataBuilders.noteContent}
                selected={isSelected}
                canEdit={canEdit}
                isEditing={isEditing}
                hasDefaultValue={hasDefaultValue}
                onBeginEditing={onBeginEditing}
                onFinishEditing={handleFinishEditing}
                backgroundColor={item.displayProperties?.cardBackgroundColor}
                backgroundColorMenu={
                  <ToolbarColorMenu item={item} saveFn={batchSaveItems} type="note" />
                }
                onClick={handleClick}
                onAIGenerate={canGenerateText ? handleAIGenerate : undefined}
              />
            </Box>
          </NoteWrapper>
        </SelectionWrapper>
        <OverflowButton
          dataPopupSource={`overflow-menu-${item.id}`}
          buttonStyle="secondary"
          icon="navigation.overflow"
          onClick={handleOverflowClick}
        />
      </Wrapper>
    </CanvasItemWrapper>
  );
};

export default CanvasNote;
